/* eslint-disable no-param-reassign */
import type { Data } from '../../specProcessor';
import type { StandardChartLayerSpec } from '../../../../core/layer';
import type { Spec as DynamicPieLayerSpec } from '../../../../layer/preset/pie/dynamicPie';
import * as utils from '../../../../util';
import {
  BaseSpec,
  ILayerTypeSpec,
  createDataTypeValidator,
  createEncodingValidator,
  createLayerProcessor
} from '../baseProcessor';

export type Spec = BaseSpec;

// 扩展ILayerTypeSpec以包含__isEncodingValid属性
interface DynamicPieLayerTypeWithFields extends ILayerTypeSpec {
  __isEncodingValid?: boolean;
}

type DynamicPieLayerType = StandardChartLayerSpec &
  DynamicPieLayerSpec &
  DynamicPieLayerTypeWithFields;

// 定义数据项类型
interface DataItem {
  [key: string]: string | number | Date | null | undefined;
}

// 创建动态饼图数据类型验证器
const validateDynamicPieDataTypes = createDataTypeValidator('dynamicPie', [
  { field: 'x', type: 'string' },
  { field: 'y', type: 'number' },
  { field: 'date', type: 'date', required: false }
]);

// 创建动态饼图编码验证器
const validateDynamicPieEncoding = createEncodingValidator([
  { field: 'x' },
  { field: 'y' },
  { field: 'date', required: false }
]);

// 额外的检查函数
const additionalDynamicPieChecks = (layer: DynamicPieLayerType, data: Data) => {
  const { encoding } = layer;
  const dataIndex = layer.dataIndex || 0;
  const values = (data[dataIndex]?.values as DataItem[]) || [];
  const dateField = encoding.date || 'date';

  if (!values.length) {
    return;
  }

  // 获取所有日期值
  const dateValues = new Set();
  values.forEach(item => {
    if (item[dateField]) {
      dateValues.add(item[dateField]);
    }
  });

  // 转换为数组以使用forEach
  const dateArray = Array.from(dateValues);

  dateArray.forEach(dateValue => {
    const dataPerDate = values.filter(item => item[dateField] === dateValue);

    // 检查每个时间点的数据是否超过10条
    if (dataPerDate.length > 10) {
      utils.logger.warn(
        'warning',
        `The "${layer.type}" chart works best with fewer than 10 data points per time period for better readability`
      );
    }

    // 检查每个时间点是否至少有2条数据
    if (dataPerDate.length < 2) {
      utils.logger.warn(
        'warning',
        `The "${layer.type}" chart requires at least 2 data points per time period to show distribution`
      );
    }

    // 检查是否有重复类别
    const categories = new Set();

    dataPerDate.forEach(item => {
      if (categories.has(item[encoding.x])) {
        utils.logger.warn(
          'warning',
          `The "${layer.type}" chart should not have duplicate categories (${encoding.x}) in the same time period`
        );
        layer.__isEncodingValid = false;
      }
      categories.add(item[encoding.x]);
    });
  });
};

// 创建并导出动态饼图处理器
export const dynamicPie = createLayerProcessor<DynamicPieLayerType>(
  'dynamicPie',
  validateDynamicPieEncoding,
  validateDynamicPieDataTypes,
  additionalDynamicPieChecks
);
